package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.domain.model.AccountantExport
import com.evoapps.evoshift.domain.model.AccountantExportFormat
import com.evoapps.evoshift.domain.model.AccountantMailLimitException
import com.evoapps.evoshift.domain.model.AccountantMailUnavailableException
import com.evoapps.evoshift.domain.model.InvalidEmailException
import com.evoapps.evoshift.domain.repository.AccountantExportRepository
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.longOrNull
import kotlinx.serialization.json.put

/** SH-117: the exportForAccountant callable (functions/accountantExport.js). */
class AccountantExportRepositoryImpl(private val functions: CloudFunctionsClient) : AccountantExportRepository {

    override suspend fun export(
        workplaceId: String,
        year: Int,
        month: Int,
        format: AccountantExportFormat,
        language: String,
        emailTo: String?
    ): Result<AccountantExport> = try {
        val answer = functions.call(
            "exportForAccountant",
            buildJsonObject {
                put("workplaceId", workplaceId)
                put("year", year)
                put("month", month)
                put("format", format.name)
                put("language", language)
                if (emailTo != null) put("emailTo", emailTo)
            }
        )
        Result.success(parse(answer))
    } catch (e: CloudFunctionsException) {
        Result.failure(
            when (e.code) {
                "resource-exhausted" -> AccountantMailLimitException()
                // The only precondition: e-mail is not set up on the server.
                "failed-precondition" -> AccountantMailUnavailableException()
                "invalid-argument" -> if (emailTo != null) InvalidEmailException() else e
                else -> e
            }
        )
    } catch (e: Exception) {
        Result.failure(e)
    }

    companion object {
        /** {url, expiresAt, fileName, sizeBytes, people, withoutPayrollId, emailedTo, attached}. */
        fun parse(result: JsonElement): AccountantExport {
            val o: JsonObject = result.jsonObject
            fun text(key: String) = o[key]?.jsonPrimitive?.contentOrNull
            return AccountantExport(
                url = text("url")?.takeIf { it.isNotEmpty() } ?: error("The export has no download link"),
                expiresAt = text("expiresAt").orEmpty(),
                fileName = text("fileName").orEmpty(),
                sizeBytes = o["sizeBytes"]?.jsonPrimitive?.longOrNull ?: 0L,
                people = o["people"]?.jsonPrimitive?.intOrNull ?: 0,
                withoutPayrollId = o["withoutPayrollId"]?.jsonArray?.mapNotNull { it.jsonPrimitive.contentOrNull }.orEmpty(),
                emailedTo = text("emailedTo")?.takeIf { it.isNotEmpty() },
                attached = o["attached"]?.jsonPrimitive?.booleanOrNull ?: false
            )
        }
    }
}
